import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, WritableSignal, computed, effect, inject, signal, } from '@angular/core';
import { HeaderService } from '../header.service';
import { FormsModule } from '@angular/forms';
import { FilterType, FilterTypes } from './search-bar.types';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ResolutionService } from '../../resolution.service';
import { MatChipsModule } from '@angular/material/chips';
import { ProductsService } from '../../../product/products.service';
import { FilterParams } from '../../../product/product.types';
import { ActivatedRoute, Router } from '@angular/router';
import { SignalsStoreService } from '../../signals-store.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { TypesenseSearchService, SearchSuggestion } from '../../typesense-search.service';
import { EMPTY, Subject, debounceTime, distinctUntilChanged, delay, finalize, from, switchMap, take, takeUntil, tap } from 'rxjs';
import { AutocompleteProductCardComponent } from './autocomplete-product-card/autocomplete-product-card.component';
import { NotificationService } from '../../notification/notification.service';
import { OrderService } from '../../order.service';
import { LocalStorageService } from '../../local-storage.service';
import { ModalContentService } from '../../modal-content/modal-content.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isAbandonedUser, isAddressUpdateRequired, isNoAddressUser, arrayToMapMultiKey, getShopScrollElement, getCurrentPathFromUrl, findTopVisibleProductId } from '../../common/utils';
import { FireBaseProductOrder } from '../../types/order.type';
import { ProductCardV2Service } from '../../product-card-v2/product-card-v2.service';
import { ModalContentTypes } from '../../constants/modal-content-types';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-search-bar',
  imports: [
    FormsModule,
    CommonModule,
    MatChipsModule,
    AutocompleteProductCardComponent
  ],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss'
})
export class SearchBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;

  private headerService = inject(HeaderService);
  private productService = inject(ProductsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private signalsStoreService = inject(SignalsStoreService);
  private typesenseService = inject(TypesenseSearchService);
  private notificationService = inject(NotificationService);
  private orderService = inject(OrderService);
  private localStorageService = inject(LocalStorageService);
  private modalContentService = inject(ModalContentService);
  private activeModal = inject(NgbModal);
  private productCardService = inject(ProductCardV2Service);
  private resolutionService = inject(ResolutionService);
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();

  // Tracks the previous open-state so we can capture scroll exactly on the false→true edge.
  private prevSearchBarOpen = false;

  constructor() {
    // Lock body scroll while the mobile overlay is open — iOS Safari scroll-chains
    // under fixed overlays without it. Also capture the shop scroll position the moment
    // the search bar opens, so it can be restored when the user clears the search.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const open = this.headerService.displaySearchBar();
      const isMobile = this.resolutionService.isMobile();
      document.body.classList.toggle('search-overlay-open', open && isMobile);

      const openingNow = open && !this.prevSearchBarOpen;
      this.prevSearchBarOpen = open;
      if (openingNow) {
        const scroller = getShopScrollElement();
        const scrollTop = scroller?.scrollTop ?? 0;
        const path = getCurrentPathFromUrl(this.router.url);
        const topProductId = findTopVisibleProductId(scroller, scrollTop);
        // Pixel-precise restore: store how far above the anchor card the user actually was
        // (positive when the user has scrolled past the anchor's top, negative when above).
        // Restore will scroll to `anchor.offsetTop + offsetFromAnchor` instead of just
        // pinning the anchor to top — preserves the exact viewport position.
        let offsetFromAnchor = 0;
        if (topProductId && scroller) {
          const anchor = scroller.querySelector<HTMLElement>(`[id="${topProductId}"]`);
          if (anchor) offsetFromAnchor = scrollTop - anchor.offsetTop;
        }
        this.signalsStoreService.preSearchScroll.set({
          path,
          scrollTop,
          topProductId,
          offsetFromAnchor,
        });
      }
    });
  }

  searchBarContent: WritableSignal<string> =
    this.headerService.searchBarContent;

  filters: WritableSignal<Map<string, { type: FilterTypes; value: string }>> = signal(new Map());

  filterTypes: Map<FilterTypes, WritableSignal<number>> = new Map();

  // -1 means no row pre-selected — Enter falls through to "search all" default.
  selectedIndex = -1;

  // Typesense autocomplete
  suggestions = this.typesenseService.suggestions;
  popularProducts = this.typesenseService.popularProducts;
  popularSearches = this.typesenseService.popularSearches;
  querySuggestions = this.typesenseService.querySuggestions;
  recentSearches = this.typesenseService.recentSearches;
  addingToCartProductId: WritableSignal<string | null> = signal(null);
  isLoadingSuggestions: WritableSignal<boolean> = signal(false);
  showDropdown: WritableSignal<boolean> = signal(false);
  isLoadingPopular: WritableSignal<boolean> = signal(false);
  private searchInput$ = new Subject<string>();
  private popularProductsLoaded = false;

  getFilterChipRemovedKey$ = toObservable(this.signalsStoreService.getFilterChipRemoved);

  isInShopComponent = computed(() => this.signalsStoreService.getIsInShopComponent());
  hasSession = this.signalsStoreService.hasSession;

  isQuerying = computed(() => {
    const q = this.searchBarContent();
    return !!q && q.length >= 2;
  });

  suggestionsTotalFound = this.typesenseService.suggestionsTotalFound;
  private suggestionsReturnedCount = this.typesenseService.suggestionsReturnedCount;

  /**
   * Trailing "Search all N products" tile in the grid. Only worth showing when the query
   * actually matched more documents than the dropdown had room for — with everything already
   * on screen the tile would just repeat the left column's "Search all" row. Both sides of
   * the comparison are document counts, never `suggestions().length`, which counts expanded
   * variant cards and would read as "more results" for a single multi-package product.
   */
  showSearchAllCard = computed(() =>
    this.isQuerying()
    && this.suggestions().length > 0
    && this.suggestionsTotalFound() > this.suggestionsReturnedCount()
  );

  /**
   * Flat keyboard-navigable model. Order matches visual order: left column items first,
   * then right column products, then the trailing search-all tile when it is shown.
   */
  flatNavItems = computed<{
    leftItems: { kind: 'query' | 'recent' | 'popular' | 'search-all'; value: string }[];
    products: SearchSuggestion[];
    hasSearchAllCard: boolean;
    total: number;
  }>(() => {
    const querying = this.isQuerying();
    const leftItems: { kind: 'query' | 'recent' | 'popular' | 'search-all'; value: string }[] = [];

    if (querying) {
      for (const s of this.querySuggestions()) leftItems.push({ kind: 'query', value: s });
      leftItems.push({ kind: 'search-all', value: this.searchBarContent() });
    } else {
      for (const s of this.recentSearches()) leftItems.push({ kind: 'recent', value: s });
      for (const s of this.popularSearches()) leftItems.push({ kind: 'popular', value: s });
    }

    const products = querying ? this.suggestions() : this.popularProducts();
    const hasSearchAllCard = this.showSearchAllCard();
    return {
      leftItems,
      products,
      hasSearchAllCard,
      total: leftItems.length + products.length + (hasSearchAllCard ? 1 : 0),
    };
  });

  totalSuggestionItems = computed(() => this.flatNavItems().total);

  ngOnInit(): void {
    this.setUpFilterTypesMap();
    this.setupTypesenseAutocomplete();
    this.typesenseService.loadRecentSearches();

    // Hydrate input from ?q= so the search query stays visible after refresh
    // or when arriving on /shop?q= directly. router.parseUrl reads the live URL,
    // not this component's ActivatedRoute (search-bar lives in the header).
    const initialQuery = this.router.parseUrl(this.router.url).queryParams['q'];
    if (initialQuery) this.searchBarContent.set(initialQuery);

    this.getFilterChipRemovedKey$.subscribe((key) => {
      if (!key) return;
      this.remove(key);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Belt-and-suspenders: drop the lock even if the effect didn't fire teardown.
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('search-overlay-open');
    }
  }

  private setupTypesenseAutocomplete(): void {
    // switchMap cancels the previous in-flight request when the query changes,
    // so a slow response for "ap" can never overwrite a fresher one for "appl".
    this.searchInput$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap(query => {
          if (query.length < 2) {
            this.typesenseService.clearSuggestions();
            this.isLoadingSuggestions.set(false);
            return EMPTY;
          }
          this.isLoadingSuggestions.set(true);
          return from(this.typesenseService.getAutocompleteSuggestions(query, 6))
            .pipe(finalize(() => this.isLoadingSuggestions.set(false)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    const hasProducerFilter = !!this.signalsStoreService.filterByProducer();
    if (hasProducerFilter) {
      this.searchBarContent.set(this.signalsStoreService.filterByProducer());
      // Add the producer chip without focusing the input — otherwise the
      // popular searches dropdown deploys on top of the filtered results
      // when the user lands on /shop from a producer CTA.
      this.add(FilterTypes.producers, false);
      return;
    }

    if (this.searchInput && this.searchInput.nativeElement) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    const total = this.totalSuggestionItems();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (total === 0) return;
      // -1 (no selection) wraps to first item; otherwise advance with wraparound.
      this.selectedIndex = this.selectedIndex < 0 ? 0 : (this.selectedIndex + 1) % total;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (total === 0) return;
      // From -1, ArrowUp jumps to the last item (common combobox idiom).
      this.selectedIndex = this.selectedIndex < 0 ? total - 1 : (this.selectedIndex - 1 + total) % total;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.activateSelection();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
      this.searchInput.nativeElement.blur();
    } else if (event.key === 'Home' && this.selectedIndex >= 0) {
      event.preventDefault();
      this.selectedIndex = 0;
    } else if (event.key === 'End' && this.selectedIndex >= 0) {
      event.preventDefault();
      this.selectedIndex = total - 1;
    }
  }

  /** Resolves the current selectedIndex (or query) into an action. */
  private activateSelection(): void {
    const { leftItems, products } = this.flatNavItems();
    const query = (this.searchBarContent() || '').trim();

    // Default action when nothing is keyboard-selected: search-all.
    if (this.selectedIndex < 0) {
      if (query.length >= 2) this.navigateToSearch(query);
      return;
    }

    if (this.selectedIndex < leftItems.length) {
      const item = leftItems[this.selectedIndex];
      this.navigateToSearch(item.value);
      return;
    }

    const productIdx = this.selectedIndex - leftItems.length;
    const product = products[productIdx];
    if (product) {
      this.onSuggestionSelected(product);
      return;
    }

    // Past the last card: the trailing search-all tile, which repeats the left column's action.
    if (this.selectedIndex === leftItems.length + products.length && query.length >= 2) {
      this.navigateToSearch(query);
    }
  }

  private closeDropdown(): void {
    this.typesenseService.clearSuggestions();
    this.showDropdown.set(false);
    this.selectedIndex = -1;
  }

  onSuggestionSelected(suggestion: SearchSuggestion): void {
    this.typesenseService.clearSuggestions();
    this.searchBarContent.set('');
    this.showDropdown.set(false);
    this.selectedIndex = -1;

    // Navigate to product detail page
    if (suggestion.productUrl) {
      this.router.navigateByUrl(`/shop/${suggestion.productUrl}`);
    }
    this.closeMobileOverlay();
  }

  navigateToSearch(query: string): void {
    const trimmed = (query || '').trim();
    if (!trimmed) return;

    // Persist for next session and surface in the "Recent searches" section.
    this.typesenseService.addRecentSearch(trimmed);

    this.typesenseService.clearSuggestions();
    // Reset the debounce-pipeline memory so retyping the same query after navigation
    // refetches (distinctUntilChanged would otherwise drop the same-value emission).
    this.searchInput$.next('');
    this.showDropdown.set(false);
    this.selectedIndex = -1;
    this.searchInput.nativeElement.blur();

    // Keep the query visible in the input so users can refine it on the
    // results page without retyping.
    this.searchBarContent.set(trimmed);

    this.signalsStoreService.isInSearchMode.set(true);
    this.signalsStoreService.searchModeQuery.set(trimmed);

    // If already in shop, don't navigate — ShopComponent reacts to signal changes
    if (!this.isInShopComponent()) {
      this.router.navigate(['/shop'], { queryParams: { q: trimmed } });
    }
    this.closeMobileOverlay();
  }

  onQuerySuggestionClick(suggestion: string): void {
    this.navigateToSearch(suggestion);
  }

  onItemHover(index: number): void {
    // Sync hover with keyboard state so the active row matches the pointer.
    this.selectedIndex = index;
  }

  onLeftItemRemove(event: Event, value: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.typesenseService.removeRecentSearch(value);
  }

  onClearRecent(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.typesenseService.clearRecentSearches();
  }

  async onSearchFocus(): Promise<void> {
    this.showDropdown.set(true);

    const query = this.searchBarContent();

    // Returning to the input on the results page: input still has the query (preserved through
    // navigation) but suggestions were cleared. Refetch directly — debounced pipeline's
    // distinctUntilChanged would drop a same-value emission.
    if (query && query.length >= 2 && this.suggestions().length === 0 && !this.isLoadingSuggestions()) {
      this.isLoadingSuggestions.set(true);
      try {
        await this.typesenseService.getAutocompleteSuggestions(query, 6);
      } finally {
        this.isLoadingSuggestions.set(false);
      }
      return;
    }

    // Cold-load popular content for the empty-input state.
    if (!this.popularProductsLoaded && !this.isLoadingPopular() && !query) {
      this.isLoadingPopular.set(true);
      await Promise.all([
        this.typesenseService.getPopularProducts(8),
        this.typesenseService.getPopularSearches(6)
      ]);
      this.popularProductsLoaded = true;
      this.isLoadingPopular.set(false);
    }
  }

  onSearchBlur(event: FocusEvent): void {
    // Mobile overlay short-circuit: the input loses focus when the user taps to
    // scroll the products row; closing the dropdown there would feel broken.
    // Only the explicit Cancel button dismisses the overlay.
    if (this.resolutionService.isMobile() && this.headerService.displaySearchBar()) return;

    // The dropdown's interactive items use (mousedown)+preventDefault to keep
    // focus on the input, so a real blur means the user moved focus elsewhere.
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('.autocomplete-dropdown')) return;
    this.showDropdown.set(false);
    this.selectedIndex = -1;
  }

  onProductCardClick(product: SearchSuggestion): void {
    this.typesenseService.clearSuggestions();
    this.searchBarContent.set('');
    this.showDropdown.set(false);

    if (product.productUrl) {
      this.router.navigateByUrl(`/shop/${product.productUrl}`);
    }
    this.closeMobileOverlay();
  }

  /**
   * While the market is closed only subscriptions can be created — the shop card enforces
   * this by disabling its Add button (product-card-v2-main.component.html:287). The dropdown
   * has no subscribe UI, so blocked products route to the PDP instead. Without this the
   * search bar would be a hole in the rule: addProductToFirebaseOrder writes straight to
   * Firebase with no server-side market guard.
   */
  #isBlockedByClosedMarket(): boolean {
    return !this.signalsStoreService.marketStatus().isOpen;
  }

  async onProductAddToCart(product: SearchSuggestion): Promise<void> {
    if (!this.hasSession() || !product.canQuickAdd || product.stock?.isOut
        || this.#isBlockedByClosedMarket()) {
      // Anonymous, variant-required, OOS, or market closed — route to PDP instead of
      // attempting quick-add.
      if (product.productUrl) {
        this.router.navigateByUrl(`/shop/${product.productUrl}`);
        this.showDropdown.set(false);
        this.searchBarContent.set('');
        this.typesenseService.clearSuggestions();
      }
      this.closeMobileOverlay();
      return;
    }

    // Quick-add for simple one-time products
    // Validation checks
    if (isAbandonedUser(this.localStorageService, this.modalContentService, this.activeModal)) return;
    if (isAddressUpdateRequired(this.localStorageService, this.modalContentService, this.activeModal, this.router)) return;
    if (isNoAddressUser(this.localStorageService, this.modalContentService, this.activeModal, this.router)) return;

    // If the user has their order marked as skipped, prompt to unskip
    const hasSkipped = await this.#hasSkippedOrder();
    if (hasSkipped) return;

    // Fetch full product details and add to cart
    const productId = parseInt(product.parentProductId || product.id, 10);
    this.addingToCartProductId.set(product.id);

    this.productService.getProductsDetailsByIds([productId])
      .pipe(
        take(1),
        tap((products: any) => {
          const fullProduct = Array.isArray(products) ? products[0] : products[productId];
          if (!fullProduct) {
            this.notificationService.show({ text: 'Could not load product details.', type: 'error' });
            this.addingToCartProductId.set(null);
            return;
          }

          // Build stock map from product's stockInfo and get variant — same shape
          // ProductCardV2Component builds before it calls getVariant().
          const stockMap = arrayToMapMultiKey(['productId', 'attribute.id', 'attribute.value.id'], fullProduct.stockInfo || []);
          const selectedAttribute = product.attributeId && product.attributeValueId
            ? { id: product.attributeId, value: { id: product.attributeValueId } }
            : null;
          const variant = this.productCardService.getVariant(stockMap, fullProduct.id, selectedAttribute);

          // The dropdown expands a multi-package product into one card per package
          // (expandSuggestions), so the package the user actually clicked travels on the
          // suggestion as `packageId`. It has to be resolved against the live product here:
          // reading `packages[0]` instead added the first package — and its price — no matter
          // which card was clicked.
          const packages: any[] = fullProduct.packages ?? [];
          const pack = product.packageId != null
            ? packages.find((p: any) => +p.id === product.packageId) ?? null
            : (packages[0] ?? null);

          if (product.packageId != null && !pack) {
            // The indexed package is no longer on the live product (removed or re-created
            // since the last Typesense sync). Falling back to another package would quietly
            // add a different size, so send the user to the PDP to pick again.
            this.#bailToProductPage(
              product,
              `That option is no longer available for ${fullProduct.name.trim()} — choose one on the product page.`
            );
            return;
          }

          // Live, package-aware stock check. Two reasons it can't reuse the card's `stock`
          // state: the indexed stock it was rendered from can be minutes old, and one add of
          // an "8 count" package reserves 8 units, not 1. Mirrors ProductCardV2Component,
          // where validateProductAndSetUpVariant() treats a variant with no stock entry as a
          // missing variant and isOutOfStock() multiplies the quantity by the selected
          // package (product-card-v2.component.ts:715).
          const unitsPerAdd = pack?.quantity > 0 ? pack.quantity : 1;
          const stockAfterAdd = (variant?.stock ?? 0) - unitsPerAdd;
          // `limit` is a negative backorder buffer; 0 means strict, no overselling.
          const fitsInStock = !!variant
            && (stockAfterAdd >= 0 || (variant.limit !== 0 && stockAfterAdd >= variant.limit));

          if (!variant || !variant.stock || !fitsInStock) {
            const label = product.variantLabel ? ` (${product.variantLabel})` : '';
            this.#bailToProductPage(
              product,
              `${fullProduct.name.trim()}${label} is sold out — choose an alternative on the product page.`
            );
            return;
          }

          const productToAdd = this.#buildFirebaseProductToAdd(fullProduct, variant, pack);

          // Firebase-first cart: addProductToFirebaseOrder writes the line synchronously and
          // fires the Klaviyo ProductAddedToCart event itself, so nothing to track here.
          // showSuccessMessage stays false — we show the richer per-product toast below.
          try {
            this.orderService.addProductToFirebaseOrder(productToAdd, false);
          } catch {
            this.notificationService.show({ text: 'Could not add the product to your order.', type: 'error' });
            this.addingToCartProductId.set(null);
            return;
          }

          const variantLabel = product.variantLabel ? ` (${product.variantLabel})` : '';
          this.notificationService.show({
            text: `${fullProduct.name.trim()}${variantLabel} added to order!`,
            type: 'success',
            delay: 3000
          });
          this.signalsStoreService.showCartPreview.set(true);

          this.showDropdown.set(false);
          this.searchBarContent.set('');
          this.typesenseService.clearSuggestions();
          this.addingToCartProductId.set(null);
          this.closeMobileOverlay();
        })
      ).subscribe({
        error: () => this.addingToCartProductId.set(null)
      });
  }

  /**
   * Quick-add cannot complete (sold out, or the clicked package is gone from the live
   * product): say why and hand off to the PDP, where the real card offers the alternatives
   * the dropdown has no room for. Always clears the per-card spinner.
   */
  #bailToProductPage(product: SearchSuggestion, text: string): void {
    this.notificationService.show({ text, type: 'info', delay: 3500 });
    this.addingToCartProductId.set(null);
    if (product.productUrl) {
      this.showDropdown.set(false);
      this.router.navigateByUrl(`/shop/${product.productUrl}`);
    }
    this.closeMobileOverlay();
  }

  /**
   * Builds the Firebase order line for a quick-added product. Field-for-field mirror of
   * ProductCardV2Component.setUpProductToAdd() for a one-time product, so a line added from
   * the search bar is indistinguishable from one added by the card.
   */
  #buildFirebaseProductToAdd(fullProduct: any, variant: any, pack: any): Partial<FireBaseProductOrder> {
    const quantity = 1;
    // Expanded suggestions can be bundles' siblings but never bundles themselves — keep the
    // card's `product.bundle || null` shape so the stored line matches field-for-field.
    const bundle: any = fullProduct.bundle || null;
    // `pack` is the card's "selectedPackage": the package behind the clicked card, resolved
    // by the caller from the suggestion's packageId.
    // Same package-aware pricing the card applies through selectedAttributeCardConfig().
    const hasPackPricing = !!pack && pack.quantity >= 0;
    const price = hasPackPricing ? pack.price * pack.quantity : fullProduct.price;
    const originalPrice = hasPackPricing
      ? (pack.originalPrice ? pack.originalPrice * pack.quantity : price)
      : (fullProduct.originalPrice ?? price);

    return {
      updatedAt: DateTime.now().toMillis(),
      bundle,
      category: fullProduct.category,
      id: fullProduct.id,
      img: fullProduct.img,
      isASubscription: false,
      isFromCard: true,
      name: fullProduct.name,
      package: pack,
      presentation: fullProduct.presentation || null,
      price,
      originalPrice,
      productUrl: fullProduct.productUrl,
      quantity,
      subCategory: fullProduct.subcategory,
      subscription: null,
      taxes: fullProduct.taxes,
      totalPrice: quantity * (fullProduct.price as number),
      variant,
      isPublishedBundle: fullProduct.isPublishedBundle ?? false
    } as Partial<FireBaseProductOrder>;
  }

  async #hasSkippedOrder(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.orderService.odooOrder()?.isSkipped) {
        this.modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
          title: `You've skipped the week`,
          textContent: `You want to add products to your order but you skipped this week's delivery`,
          confirmButtonText: 'Unskip this week',
        }, { size: 'md' }).closed
          .subscribe((res: { confirm: boolean } | null) => {
            if (!res?.confirm) return resolve(true);

            const args = {
              orderId: this.orderService.odooOrder()?.id,
              isSkipping: false,
              donationAmount: 0,
            };

            // getStock=false matches ProductCardV2Component.hasSkippedOrder() — the
            // quick-add refetches the product details (and its stock) right after.
            this.orderService.skipOrder(args, false).pipe(delay(1000)).subscribe(() => resolve(false));
          });
      } else {
        return resolve(false);
      }
    });
  }

  // Header text for products section — uses isQuerying() (kept on the
  // class) so the template can read it without recomputation.
  productsHeaderText = computed(() => this.isQuerying() ? 'Products' : 'Popular Products');

  private setUpFilterTypesMap() {
    for (const type of Object.values(FilterTypes)) {
      const key: any = type;
      this.filterTypes.set(key, signal(0));
    }
  }

  clearSearchInput(): void {
    this.searchBarContent.set('');
    this.typesenseService.clearSuggestions();
    // Same distinctUntilChanged reset as navigateToSearch — see comment there.
    this.searchInput$.next('');
    this.searchInput.nativeElement.focus();
  }

  onSearchSubmit(): void {
    const query = this.searchBarContent();
    if (query && query.length >= 2) {
      this.navigateToSearch(query);
    }
  }

  inputSearchTypeHandler(event: any) {
    const value = event.target.value;
    // Reopen dropdown if closed (e.g. after a search navigation while input kept focus)
    if (!this.showDropdown()) this.showDropdown.set(true);
    // Emit to Typesense autocomplete
    this.searchInput$.next(value);
    // Drop any keyboard selection so Enter falls through to "search all".
    this.selectedIndex = -1;
  }

  onFilterTypeSelected(filterType: FilterTypes) {
    const filterExists = this.filters().get(
      `${filterType}${this.searchBarContent()}`
    );
    if (filterExists) return;

    const current = this.filterTypes.get(filterType);
    if (current) {
      current.update((value: number) => value + 1);
      this.add(filterType);
    }
  }

  add(filterType: FilterTypes, shouldFocus: boolean = true): void {
    const value = (this.searchBarContent() || '').trim();
    if (value) {
      this.filters().set(`${filterType}${value}`, { type: filterType, value });
      this.signalsStoreService.setFilters(this.filters());
      this.searchBarContent.set('');
      if (shouldFocus) {
        this.searchInput.nativeElement.focus();
      }
      this.search();
    }
  }

  remove(key: string): void {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.filters().delete(key);
      this.signalsStoreService.setFilters(this.filters());

      const current = this.filterTypes.get(type);
      if (current) current.update((value: number) => value - 1);

      this.searchInput.nativeElement.focus();

      if (this.filters().size > 0) this.search();
      else this.headerService.clearFilters(false);
    }
  }

  search() {
    const filterParams: FilterParams[] = this.setUpFilterParams();

    // If the current loaded component is not _ShopComponent, should redirect the client to it:
    this.headerService.setFiltering(true);
    if (!this.isInShopComponent()) {
      this.router.navigateByUrl('/shop');
    }
    this.productService.getProductsByFilter(filterParams);
  }

  private setUpFilterParams(): FilterParams[] {
    const params: Map<FilterType, string[]> = new Map();
    this.filters().forEach(
      (value: { type: FilterTypes; value: string }, key: string) => {
        if (!params.has(value.type)) {
          params.set(value.type, [value.value]);
        } else {
          const current = params.get(value.type);
          if (current) {
            current.push(value.value);
            params.set(value.type, current);
          }
        }
      }
    );

    return Array.from(params.entries()).map(([type, values]) => ({
      type,
      values,
    }));
  }

  /**
   * Dismiss the mobile full-page overlay after a navigation. On desktop the
   * inline search bar stays mounted; only the mobile overlay steps aside.
   */
  private closeMobileOverlay(): void {
    if (this.resolutionService.isMobile() && this.headerService.displaySearchBar()) {
      this.headerService.displaySearchBar.set(false);
    }
  }

  /**
   * Mobile overlay Cancel: clear input + suggestions, then delegate to
   * headerService.clearFilters — exits search mode, resets filtering state,
   * and on /shop reloads the catalog.
   */
  cancelMobile(): void {
    this.searchBarContent.set('');
    this.typesenseService.clearSuggestions();
    this.searchInput$.next('');
    this.headerService.clearFilters(true);
  }
}
